import { utcToZonedWallClock, zonedTimeToUtc } from '@/lib/utils/timezone';

/**
 * The 30 days between asking to delete an account and the deletion (CEO
 * decision, 24 Oct 2026), for athletes and organisers alike.
 *
 * The deletion is due at the START of a calendar day, never at a time of day:
 * people are told a date ("your account will be deleted on 24 November"), and
 * the daily cron runs at 03:00 UTC, which is after midnight in Dublin in both
 * winter and summer time. So the account goes in the early hours of the date
 * it was given, and the date is true to the day.
 *
 *   asked at          24 Oct 15:00
 *   + 30 days         23 Nov 15:00
 *   next midnight     24 Nov 00:00 Dublin   <- deletion_scheduled_for
 *   the cron          24 Nov 03:00 UTC      <- the account is deleted
 *
 * Rounding UP to the next midnight means nobody ever gets less than 30 full
 * days. The date is Fitvent's, not an event's, so it is worked out in Dublin
 * (where Fitvent is), with the clock change handled by the timezone helpers.
 */
export const DELETION_WINDOW_DAYS = 30;
export const PLATFORM_TIMEZONE = 'Europe/Dublin';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Shift a 'YYYY-MM-DD' calendar date by whole days, free of any timezone. */
function shiftDay(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/** When an account asked to be deleted at `requestedAt` is due to go. */
export function deletionDueFor(requestedAt: Date): Date {
  const plus = new Date(requestedAt.getTime() + DELETION_WINDOW_DAYS * DAY_MS);
  const { date } = utcToZonedWallClock(plus, PLATFORM_TIMEZONE);
  return zonedTimeToUtc(shiftDay(date, 1), '00:00', PLATFORM_TIMEZONE);
}

/** The date people are told, e.g. "24 November 2026". */
export function formatDeletionDate(due: Date | string): string {
  const d = typeof due === 'string' ? new Date(due) : due;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: PLATFORM_TIMEZONE,
  }).format(d);
}
